<template>
    <div class="rider-third-party-review-page public_box">
        <div class="rider-third-party-review-page__header">
            <div>
                <div class="rider-third-party-review-page__nav">
                    <el-button size="mini" plain @click="goWorkbench">返回核验工作台</el-button>
                    <el-button size="mini" type="text" @click="goDuplicateReview">切换到重复图片核验</el-button>
                    <el-button size="mini" type="text" @click="goSimilarImageReview">切换到相似图片核验</el-button>
                    <el-button size="mini" type="text" @click="goClaimRiskReview">切换到理算风险核验</el-button>
                </div>
                <h2>骑手三者交叉核验页面</h2>
                <p>集中查看同一身份证在不同案件中分别作为骑手和三者出现的命中组，支持案件跳转、批量判定与历史追溯。</p>
            </div>
            <div class="rider-third-party-review-page__filters">
                <el-switch
                    v-model="filters.onlyPending"
                    active-text="只看待核验"
                    @input="handleOnlyPendingToggle"
                />
                <el-radio-group
                    v-model="filters.reviewStatus"
                    size="mini"
                    :disabled="filters.onlyPending"
                    @input="handleReviewStatusChange"
                >
                    <el-radio-button label="pending">待定</el-radio-button>
                    <el-radio-button label="useful">有用</el-radio-button>
                    <el-radio-button label="not-useful">无用</el-radio-button>
                    <el-radio-button label="all">全部</el-radio-button>
                </el-radio-group>
                <el-radio-group
                    v-model="filters.amountComplete"
                    size="mini"
                    @input="handleAmountCompleteChange"
                >
                    <el-radio-button label="all">全部金额</el-radio-button>
                    <el-radio-button label="complete">金额完整</el-radio-button>
                    <el-radio-button label="incomplete">金额不完整</el-radio-button>
                </el-radio-group>
            </div>
        </div>

        <el-alert
            v-if="errorMessage"
            type="error"
            :closable="false"
            :title="errorMessage"
            class="rider-third-party-review-page__alert"
        />

        <el-empty
            v-if="!loading && !records.length"
            description="当前无可核验记录"
        />

        <div v-if="records.length" class="rider-third-party-review-page__batch-bar">
            <div class="rider-third-party-review-page__batch-summary">
                当前页共 {{ records.length }} 条，可一键批量判断
            </div>
            <div class="rider-third-party-review-page__batch-actions">
                <el-radio-group
                    v-model="batchDecision"
                    size="mini"
                    class="rider-third-party-review-page__batch-group"
                >
                    <el-radio-button label="pending">待定</el-radio-button>
                    <el-radio-button label="useful">有用</el-radio-button>
                    <el-radio-button label="not-useful">无用</el-radio-button>
                </el-radio-group>
                <el-button
                    size="mini"
                    type="primary"
                    :loading="batchSaving"
                    @click="handleBatchSaveConfirm"
                >
                    当前页批量保存
                </el-button>
            </div>
        </div>

        <div v-loading="loading" class="rider-third-party-review-page__list">
            <div
                v-for="record in records"
                :key="record.riskEventId"
                class="rider-third-party-review-page__card"
            >
                <div class="rider-third-party-review-page__main">
                    <div class="rider-third-party-review-page__top-row">
                        <div class="rider-third-party-review-page__identity">
                            <span class="rider-third-party-review-page__label">身份证</span>
                            <span>{{ record.idCard || "-" }}</span>
                            <el-tag size="mini" type="warning">
                                {{ resolveStatusLabel(record.reviewStatus) }}
                            </el-tag>
                            <el-tag size="mini" :type="record.amountCompleteFlag ? 'success' : 'danger'">
                                {{ record.amountCompleteFlag ? "金额完整" : "金额不完整" }}
                            </el-tag>
                        </div>
                        <div class="rider-third-party-review-page__trace">
                            <span>规则摘要：{{ record.ruleSummary || "-" }}</span>
                        </div>
                    </div>
                    <div class="rider-third-party-review-page__code-grid">
                        <div class="rider-third-party-review-page__code-item">
                            <span class="rider-third-party-review-page__label">总案件数</span>
                            <span>{{ record.validCaseCount || 0 }}</span>
                        </div>
                        <div class="rider-third-party-review-page__code-item">
                            <span class="rider-third-party-review-page__label">骑手案件数</span>
                            <span>{{ record.riderCaseCount || 0 }}</span>
                        </div>
                        <div class="rider-third-party-review-page__code-item">
                            <span class="rider-third-party-review-page__label">三者案件数</span>
                            <span>{{ record.thirdPartyCaseCount || 0 }}</span>
                        </div>
                        <div class="rider-third-party-review-page__code-item">
                            <span class="rider-third-party-review-page__label">累计金额</span>
                            <span>{{ formatAmount(record.totalPayAmount) }}</span>
                        </div>
                        <div class="rider-third-party-review-page__code-item">
                            <span class="rider-third-party-review-page__label">生成时间</span>
                            <span>{{ record.createdAt || "-" }}</span>
                        </div>
                        <div class="rider-third-party-review-page__code-item">
                            <span class="rider-third-party-review-page__label">最近原因</span>
                            <span>{{ record.lastReasonDesc || "-" }}</span>
                        </div>
                    </div>
                    <div class="rider-third-party-review-page__case-row">
                        <span class="rider-third-party-review-page__label">骑手案件</span>
                        <div class="rider-third-party-review-page__case-tags">
                            <el-button
                                v-for="caseNo in resolveCaseNoList(record.riderCaseNoList)"
                                :key="`${record.riskEventId}-r-${caseNo}`"
                                type="text"
                                @click="handleOpenWorkbench(caseNo)"
                            >
                                {{ caseNo }}
                            </el-button>
                        </div>
                    </div>
                    <div class="rider-third-party-review-page__case-row">
                        <span class="rider-third-party-review-page__label">三者案件</span>
                        <div class="rider-third-party-review-page__case-tags">
                            <el-button
                                v-for="caseNo in resolveCaseNoList(record.thirdPartyCaseNoList)"
                                :key="`${record.riskEventId}-t-${caseNo}`"
                                type="text"
                                @click="handleOpenWorkbench(caseNo)"
                            >
                                {{ caseNo }}
                            </el-button>
                        </div>
                    </div>
                    <div class="rider-third-party-review-page__meta">
                        <span>排除摘要：{{ record.excludeFlags || "无" }}</span>
                        <span v-if="record.reviewUpdatedAt">最近处理：{{ record.reviewUpdatedAt }}</span>
                    </div>
                    <div v-if="historyVisibleMap[record.riskEventId]" class="rider-third-party-review-page__history">
                        <div class="rider-third-party-review-page__history-title">历史变更记录和原因</div>
                        <el-timeline v-if="(historyMap[record.riskEventId] || []).length">
                            <el-timeline-item
                                v-for="item in historyMap[record.riskEventId]"
                                :key="`${record.riskEventId}-${item.historyId}`"
                                :timestamp="item.changedAt"
                            >
                                <div class="rider-third-party-review-page__history-reason">
                                    {{ item.reasonDesc || item.reasonCode || "无原因" }}
                                </div>
                                <div class="rider-third-party-review-page__history-detail">
                                    {{ buildHistorySummary(item) }}
                                </div>
                            </el-timeline-item>
                        </el-timeline>
                        <el-empty
                            v-else-if="!historyLoadingMap[record.riskEventId]"
                            :image-size="60"
                            description="暂无历史"
                        />
                    </div>
                </div>
                <div class="rider-third-party-review-page__side">
                    <el-radio-group
                        v-model="draftStatusMap[record.riskEventId]"
                        size="mini"
                        class="rider-third-party-review-page__decision-group"
                    >
                        <el-radio-button label="pending">待定</el-radio-button>
                        <el-radio-button label="useful">有用</el-radio-button>
                        <el-radio-button label="not-useful">无用</el-radio-button>
                    </el-radio-group>
                    <el-button
                        size="mini"
                        type="primary"
                        :loading="savingRecordId === record.riskEventId"
                        @click="handleSave(record)"
                    >
                        保存
                    </el-button>
                    <el-button size="mini" @click="toggleHistory(record)">
                        {{ historyVisibleMap[record.riskEventId] ? "收起历史" : "查看历史" }}
                    </el-button>
                </div>
            </div>
        </div>

        <div class="rider-third-party-review-page__pagination">
            <el-pagination
                background
                layout="prev, pager, next"
                :current-page="filters.pageNo"
                :page-size="filters.pageSize"
                :total="total"
                @current-change="handlePageChange"
            />
        </div>
    </div>
</template>

<script>
import { buildDuplicateCaseJumpUrl } from "@/views/calculatorTool/medicalThreeColumn.duplicateCase";
import {
    getRiderThirdPartyReviewHistory,
    getRiderThirdPartyReviewRecords,
    saveRiderThirdPartyReviewBatchDecision,
    saveRiderThirdPartyReviewDecision,
} from "@/api/riderThirdPartyReview";

export default {
    name: "RiderThirdPartyReviewPage",
    inject: {
        injectedInterfaceUrl: {
            from: "interfaceUrl",
            default: "",
        },
    },
    data() {
        return {
            loading: false,
            savingRecordId: null,
            batchSaving: false,
            records: [],
            total: 0,
            errorMessage: "",
            batchDecision: "pending",
            draftStatusMap: {},
            historyMap: {},
            historyVisibleMap: {},
            historyLoadingMap: {},
            filters: {
                reviewStatus: "pending",
                amountComplete: "all",
                onlyPending: true,
                pageNo: 1,
                pageSize: 20,
            },
        };
    },
    created() {
        this.hydrateFiltersFromRoute();
        this.fetchRecords();
    },
    methods: {
        getResolvedInterfaceUrl() {
            return this.injectedInterfaceUrl || "";
        },
        goWorkbench() {
            this.$router.push({ path: "/calculatorTool/reviewWorkbench" });
        },
        goDuplicateReview() {
            this.$router.push({ path: "/calculatorTool/duplicateReview" });
        },
        goSimilarImageReview() {
            this.$router.push({ path: "/calculatorTool/similarImageReview" });
        },
        goClaimRiskReview() {
            this.$router.push({ path: "/calculatorTool/claimRiskReview" });
        },
        hydrateFiltersFromRoute() {
            const query = (this.$route && this.$route.query) || {};
            const onlyPending = query.onlyPending !== "false";
            this.filters.onlyPending = onlyPending;
            this.filters.reviewStatus = onlyPending ? "pending" : query.reviewStatus || "all";
            this.filters.amountComplete = query.amountComplete || "all";
            this.filters.pageNo = Number(query.pageNo) > 0 ? Number(query.pageNo) : 1;
        },
        syncRouteQuery() {
            if (!this.$router || !this.$route) {
                return;
            }
            this.$router.replace({
                query: {
                    onlyPending: this.filters.onlyPending ? "true" : "false",
                    reviewStatus: this.filters.reviewStatus,
                    amountComplete: this.filters.amountComplete,
                    pageNo: this.filters.pageNo > 1 ? String(this.filters.pageNo) : undefined,
                },
            }).catch(() => {});
        },
        formatAmount(value) {
            if (value === null || value === undefined || value === "") {
                return "-";
            }
            return Number(value).toFixed(2);
        },
        resolveCaseNoList(caseNoList) {
            if (!caseNoList) {
                return [];
            }
            return String(caseNoList)
                .split(",")
                .map((item) => item.trim())
                .filter(Boolean);
        },
        resolveStatusLabel(status) {
            if (status === "useful") {
                return "有用";
            }
            if (status === "not-useful") {
                return "无用";
            }
            return "待定";
        },
        buildHistorySummary(item) {
            const afterCases = item.afterAllCaseNoList || "-";
            return `案件全集：${afterCases}`;
        },
        handleOnlyPendingToggle(value) {
            this.filters.reviewStatus = value ? "pending" : "all";
            this.filters.pageNo = 1;
            this.syncRouteQuery();
            this.fetchRecords();
        },
        handleReviewStatusChange() {
            this.filters.pageNo = 1;
            this.syncRouteQuery();
            this.fetchRecords();
        },
        handleAmountCompleteChange() {
            this.filters.pageNo = 1;
            this.syncRouteQuery();
            this.fetchRecords();
        },
        handlePageChange(pageNo) {
            this.filters.pageNo = pageNo;
            this.syncRouteQuery();
            this.fetchRecords();
        },
        async fetchRecords() {
            this.loading = true;
            this.errorMessage = "";
            try {
                const response = await getRiderThirdPartyReviewRecords(this.getResolvedInterfaceUrl(), {
                    reviewStatus: "all",
                    reviewDecision: this.filters.onlyPending ? "pending" : (this.filters.reviewStatus || "all"),
                    amountComplete: this.filters.amountComplete,
                    pageNo: this.filters.pageNo,
                    pageSize: this.filters.pageSize,
                });
                const data = (response && response.data && response.data.data) || {};
                this.records = data.items || [];
                this.total = data.total || 0;
                this.draftStatusMap = this.records.reduce((acc, item) => {
                    acc[item.riskEventId] = item.reviewStatus || "pending";
                    return acc;
                }, {});
            } catch (error) {
                this.errorMessage = error && error.message ? error.message : "加载失败";
                this.records = [];
                this.total = 0;
            } finally {
                this.loading = false;
            }
        },
        async handleSave(record) {
            this.savingRecordId = record.riskEventId;
            try {
                const response = await saveRiderThirdPartyReviewDecision(this.getResolvedInterfaceUrl(), record.riskEventId, {
                    decision: this.draftStatusMap[record.riskEventId],
                });
                const updated = response && response.data && response.data.data;
                if (updated && updated.riskEventId) {
                    const index = this.records.findIndex((item) => item.riskEventId === record.riskEventId);
                    if (index >= 0) {
                        this.$set(this.records, index, updated);
                    }
                }
            } finally {
                this.savingRecordId = null;
            }
        },
        async handleBatchSaveConfirm() {
            if (!this.records.length) {
                return;
            }
            this.batchSaving = true;
            try {
                await saveRiderThirdPartyReviewBatchDecision(this.getResolvedInterfaceUrl(), {
                    reviewRecordIds: this.records.map((item) => item.riskEventId),
                    decision: this.batchDecision,
                });
                await this.fetchRecords();
            } finally {
                this.batchSaving = false;
            }
        },
        async toggleHistory(record) {
            const riskEventId = record.riskEventId;
            const currentVisible = !!this.historyVisibleMap[riskEventId];
            this.$set(this.historyVisibleMap, riskEventId, !currentVisible);
            if (
                currentVisible ||
                Object.prototype.hasOwnProperty.call(this.historyMap, riskEventId)
            ) {
                return;
            }
            this.$set(this.historyLoadingMap, riskEventId, true);
            try {
                const response = await getRiderThirdPartyReviewHistory(this.getResolvedInterfaceUrl(), riskEventId);
                const items = (response && response.data && response.data.data) || [];
                this.$set(this.historyMap, riskEventId, items);
            } finally {
                this.$set(this.historyLoadingMap, riskEventId, false);
            }
        },
        handleOpenWorkbench(caseNo) {
            if (!caseNo) {
                return;
            }
            const target = buildDuplicateCaseJumpUrl(caseNo);
            window.open(target, "_blank");
        },
    },
};
</script>

<style scoped>
.rider-third-party-review-page {
    padding: 24px;
}

.rider-third-party-review-page__header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    margin-bottom: 20px;
}

.rider-third-party-review-page__nav,
.rider-third-party-review-page__filters,
.rider-third-party-review-page__batch-actions,
.rider-third-party-review-page__identity,
.rider-third-party-review-page__meta,
.rider-third-party-review-page__side {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
}

.rider-third-party-review-page__side {
    align-items: center;
    justify-content: center;
    min-width: 196px;
    gap: 8px;
}

.rider-third-party-review-page__decision-group {
    width: 100%;
    display: flex;
    justify-content: center;
}

.rider-third-party-review-page__side .el-button--mini {
    width: 100%;
    height: 28px;
    line-height: 26px;
    padding: 0 10px;
    text-align: center;
    border-radius: 6px;
    font-weight: 500;
}

.rider-third-party-review-page__list {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.rider-third-party-review-page__card {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    padding: 16px;
    background: #fff;
    border: 1px solid #ebeef5;
    border-radius: 12px;
}

.rider-third-party-review-page__main {
    flex: 1;
}

.rider-third-party-review-page__top-row,
.rider-third-party-review-page__case-row,
.rider-third-party-review-page__batch-bar {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 12px;
}

.rider-third-party-review-page__code-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;
    margin-bottom: 12px;
}

.rider-third-party-review-page__code-item,
.rider-third-party-review-page__history-detail {
    color: #5e6b80;
}

.rider-third-party-review-page__history {
    margin-top: 8px;
    padding-top: 12px;
    border-top: 1px dashed #dcdfe6;
    background: #fafbfc;
    border-radius: 8px;
    padding-left: 10px;
    padding-right: 10px;
    padding-bottom: 6px;
}

.rider-third-party-review-page__history-title {
    margin: 0 0 8px;
    color: #606266;
    font-size: 12px;
    font-weight: 600;
}

.rider-third-party-review-page__history-reason {
    color: #1f2d3d;
    font-weight: 500;
    line-height: 1.5;
}

.rider-third-party-review-page__history-detail {
    line-height: 1.5;
    font-size: 12px;
}

.rider-third-party-review-page__label {
    color: #909399;
    margin-right: 6px;
}

.rider-third-party-review-page__pagination {
    margin-top: 20px;
    display: flex;
    justify-content: flex-end;
}

@media (max-width: 960px) {
    .rider-third-party-review-page__header,
    .rider-third-party-review-page__card,
    .rider-third-party-review-page__top-row,
    .rider-third-party-review-page__case-row,
    .rider-third-party-review-page__batch-bar {
        flex-direction: column;
    }

    .rider-third-party-review-page__code-grid {
        grid-template-columns: 1fr;
    }
}
</style>
